<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { IconX } from './Icons'
import { cx } from './ui'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    hint?: string
    width?: string
    suppressClose?: boolean
  }>(),
  { width: 'max-w-lg', suppressClose: false },
)

const emit = defineEmits<{ close: [] }>()
const dialogRef = ref<HTMLDialogElement | null>(null)

async function syncOpen() {
  await nextTick()
  const el = dialogRef.value
  if (!el) return
  if (props.open && !el.open) el.showModal()
  if (!props.open && el.open) el.close()
}

watch(() => props.open, syncOpen, { immediate: true })
watch(dialogRef, syncOpen)

onBeforeUnmount(() => {
  const el = dialogRef.value
  if (el?.open) el.close()
})

function onCancel(e: Event) {
  e.preventDefault()
  if (props.suppressClose) return
  emit('close')
}

function onBackdropClick(e: MouseEvent) {
  if (props.suppressClose) return
  if (e.target === dialogRef.value) emit('close')
}
</script>

<template>
  <dialog
    ref="dialogRef"
    :class="
      cx(
        'm-auto w-[calc(100vw-2rem)] rounded-lg border border-line bg-panel p-0 text-text shadow-pop',
        'backdrop:bg-[rgb(11_18_32_/_0.45)] backdrop:backdrop-blur-[2px]',
        width,
      )
    "
    @cancel="onCancel"
    @click="onBackdropClick"
  >
    <header class="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
      <div class="min-w-0">
        <h2 class="text-[14px] font-semibold">{{ title }}</h2>
        <p v-if="hint" class="mt-0.5 text-[12px] text-dim">{{ hint }}</p>
      </div>
      <button
        type="button"
        aria-label="关闭"
        class="-mr-1 rounded-md p-1.5 text-dim transition-colors hover:bg-hover hover:text-text"
        @click="emit('close')"
      >
        <IconX :size="15" />
      </button>
    </header>

    <div class="max-h-[68vh] overflow-y-auto px-4 py-3.5">
      <slot />
    </div>

    <footer v-if="$slots.footer" class="flex items-center justify-end gap-2 border-t border-line bg-raised/45 px-4 py-3">
      <slot name="footer" />
    </footer>
  </dialog>
</template>
